import React, { useState, useEffect } from "react";
import SmallScreenFeatures from "./SmallScreenFeatures";
import { SMALLFEATURES } from "../../data/constants";

type Feature = { title: string; description: string; image: string };
type FeaturesSectionProps = { features: Feature[] };

const splitTitle = (title: string) => {
  const words = title.split(" ");
  const mid   = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
};

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ features }) => {
  /* — mobile fallback (<500 px) — */
  const [isSmall, setIsSmall] = useState(false);
  useEffect(() => {
    const set = () => setIsSmall(window.innerWidth < 500);
    set();
    window.addEventListener("resize", set);
    return () => window.removeEventListener("resize", set);
  }, []);
  if (isSmall) return <SmallScreenFeatures features={SMALLFEATURES} />;

  /* — desktop / tablet layout — */
  return (
    <section className="bg-[#F5F7FA] pt-[120px] pb-16">
      {/* 80 px side gutters, no width cap */}
      <div className="w-full px-[80px] mx-auto">
        {/* header */}
        <header className="mb-12">
          <p className="font-inter font-semibold text-[22px] leading-[150%] tracking-[-0.005em] bg-gradient-to-t from-[#46C1BF] to-[#46C1BF] bg-clip-text text-transparent mb-2">
            Key features //
          </p>
          <h2 className="font-utility font-medium text-[54px] leading-[120%] tracking-[-0.01em] capitalize text-[#011621]">
            What Sets Us Apart
          </h2>
        </header>

        {/* grid: min 340 px so cards don't shrink after 1407 px */}
        <div
          className="
            grid gap-[31px]
            [grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]
          "
        >
          {features.map(({ title, description, image }, i) => {
            const [l1, l2] = splitTitle(title);
            return (
              <div
                key={i}
                className="
                  relative overflow-hidden group clip-bottom-corner
                  aspect-[6/8] bg-center bg-cover max-h-[1150px] max-w-[864px]
                "
                style={{
                  backgroundImage: `url(${
                    image ||
                    "https://dummyimage.com/600x960/cccccc/ffffff&text=Feature+Image"
                  })`,
                }}
              >
                {/* sliding text wrapper */}
                <div className="absolute inset-0 flex  mt-[362px] p-6">
                  <div className="flex flex-col gap-[21px] transition-all transform  duration-300 translate-y-0 group-hover:-translate-y-[170px]">
                    <h3 className="font-utility text-4xl font-medium leading-tight m-0 text-[#FFFFFF]">
                      {l1}
                      <br />
                      {l2}
                    </h3>
                    <p className="text-lg font-inter text-gray-200 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      {description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
